/**
 * Mapping raisons / erreurs visite Pass → clés i18n (namespace pos).
 */

export type PassVisitFailureReason =
  | 'feature_off'
  | 'merchant_not_linked'
  | 'bridge_not_configured'
  | 'no_pass'
  | 'LOYALTY_DISABLED'
  | 'PROGRAM_INCOMPLETE'
  | 'PASS_NOT_USABLE'
  | 'PASS_NOT_FOUND'
  | 'MERCHANT_NOT_FOUND'
  | 'STAMPS_ONLY'
  | 'INVALID_TICKET'
  | 'INVALID_TRANSACTION_GROUP'
  | 'BUSINESS_MERCHANT_MISMATCH'
  | 'BRIDGE_FAILED'
  | 'TIMEOUT'
  | 'UNKNOWN';

const KNOWN_REASONS = new Set<string>([
  'feature_off',
  'merchant_not_linked',
  'bridge_not_configured',
  'no_pass',
  'LOYALTY_DISABLED',
  'PROGRAM_INCOMPLETE',
  'PASS_NOT_USABLE',
  'PASS_NOT_FOUND',
  'MERCHANT_NOT_FOUND',
  'STAMPS_ONLY',
  'INVALID_TICKET',
  'INVALID_TRANSACTION_GROUP',
  'BUSINESS_MERCHANT_MISMATCH',
  'BRIDGE_FAILED',
  'TIMEOUT',
]);

export function normalizePassVisitFailureReason(
  raw: string | null | undefined
): PassVisitFailureReason {
  const code = (raw || '').trim();
  if (!code) return 'UNKNOWN';
  if (code === 'loyaltyPass_call_timeout') return 'TIMEOUT';
  if (KNOWN_REASONS.has(code)) return code as PassVisitFailureReason;
  if (code.includes('BRIDGE_FAILED')) return 'BRIDGE_FAILED';
  return 'UNKNOWN';
}

/** Clé i18n sous `pos.passLoyalty.visitErrors.*` */
export function passVisitFailureI18nKey(reason: PassVisitFailureReason): string {
  return `passLoyalty.visitErrors.${reason}`;
}

export function parseBridgeErrorMessageFromBody(
  body: string | null | undefined
): string | null {
  if (!body) return null;
  try {
    const parsed = JSON.parse(body) as { message?: unknown };
    if (typeof parsed.message === 'string' && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    /* plain text */
  }
  const trimmed = body.trim();
  return trimmed || null;
}

export type PassVisitToastKind = 'success' | 'info' | 'warning' | 'error';

export type PassVisitUiOutcome = {
  kind: PassVisitToastKind;
  i18nKey: string;
  params?: Record<string, string | number>;
  /** passId renvoyé (auto-lien) */
  passId?: string;
};

export type PassVisitProgressRowLike = {
  ruleId: string;
  name?: string;
  current: number;
  threshold: number;
};

export type PassVisitResultLike = {
  synced?: boolean;
  reason?: string;
  passId?: string;
  duplicate?: boolean;
  stampCount?: number;
  visitsRequired?: number;
  rewardEarned?: boolean;
  rewardLabel?: string;
  multiRules?: boolean;
  awards?: Array<{ ruleId: string; rewardLabel?: string }>;
  ruleCounters?: Record<string, number>;
  /** Compteurs avant visite (si fournis) — pour n’afficher que les règles touchées. */
  ruleCountersBefore?: Record<string, number>;
  progressByRule?: PassVisitProgressRowLike[];
  /** Échec technique mis en file SA (outbox). */
  outboxQueued?: boolean;
};

/**
 * Résumé toast multi-règles : « G1 1/3 · M1 0/1 ».
 * Préfère les règles incrémentées ; sinon celles avec current > 0 ; sinon toutes.
 */
export function formatPassVisitProgressSummary(
  visit: PassVisitResultLike
): string | null {
  const rows = visit.progressByRule;
  if (!rows?.length) return null;

  const before = visit.ruleCountersBefore;
  const after = visit.ruleCounters;
  let picked = rows;

  if (before && after) {
    const changed = rows.filter((r) => {
      const b = Math.max(0, Math.floor(Number(before[r.ruleId]) || 0));
      const a =
        typeof after[r.ruleId] === 'number'
          ? Math.max(0, Math.floor(after[r.ruleId]!))
          : r.current;
      return a !== b;
    });
    if (changed.length > 0) picked = changed;
  } else if (visit.awards?.length) {
    const awardIds = new Set(visit.awards.map((a) => a.ruleId));
    const awarded = rows.filter((r) => awardIds.has(r.ruleId));
    if (awarded.length > 0) picked = awarded;
  } else {
    const progressed = rows.filter((r) => r.current > 0);
    if (progressed.length > 0) picked = progressed;
  }

  const parts = picked.map((r) => {
    const label = (r.name || r.ruleId).trim() || r.ruleId;
    const current = Math.max(0, Math.floor(Number(r.current) || 0));
    const threshold = Math.max(0, Math.floor(Number(r.threshold) || 0));
    return `${label} ${current}/${threshold}`;
  });
  return parts.length > 0 ? parts.join(' · ') : null;
}

/**
 * Module inactif / non branché : pas de toast (la caisse ne doit même pas
 * appeler le bridge — filet de sécurité si un appel a quand même eu lieu).
 */
export function isSilentPassVisitFailureReason(
  reason: PassVisitFailureReason
): boolean {
  return reason === 'feature_off' || reason === 'merchant_not_linked';
}

/** Décrit le toast après recordVisit (traduire au bord UI). `null` = silence. */
export function describePassVisitOutcome(
  visit: PassVisitResultLike | null | undefined,
  err?: unknown
): PassVisitUiOutcome | null {
  if (err) {
    const msg =
      err && typeof err === 'object' && 'message' in err
        ? String((err as { message?: string }).message || '')
        : '';
    const reason = normalizePassVisitFailureReason(msg);
    if (isSilentPassVisitFailureReason(reason)) return null;
    return {
      kind: 'warning',
      i18nKey: passVisitFailureI18nKey(reason),
    };
  }
  if (!visit) {
    return {
      kind: 'warning',
      i18nKey: passVisitFailureI18nKey('UNKNOWN'),
    };
  }
  if (!visit.synced) {
    const reason = normalizePassVisitFailureReason(visit.reason);
    if (isSilentPassVisitFailureReason(reason)) return null;
    return {
      kind: 'warning',
      i18nKey: passVisitFailureI18nKey(reason),
      ...(visit.passId ? { passId: visit.passId } : {}),
    };
  }
  if (visit.rewardEarned) {
    return {
      kind: 'success',
      i18nKey: 'passLoyalty.rewardEarned',
      params: { label: visit.rewardLabel || '' },
      ...(visit.passId ? { passId: visit.passId } : {}),
    };
  }

  const rulesSummary = formatPassVisitProgressSummary(visit);

  if (visit.duplicate) {
    if (rulesSummary) {
      return {
        kind: 'info',
        i18nKey: 'passLoyalty.visitDuplicateRules',
        params: { summary: rulesSummary },
        ...(visit.passId ? { passId: visit.passId } : {}),
      };
    }
    return {
      kind: 'info',
      i18nKey: 'passLoyalty.visitDuplicate',
      params: {
        stamps: visit.stampCount ?? 0,
        required: visit.visitsRequired ?? 0,
      },
      ...(visit.passId ? { passId: visit.passId } : {}),
    };
  }

  if (rulesSummary) {
    return {
      kind: 'success',
      i18nKey: 'passLoyalty.visitRecordedRules',
      params: { summary: rulesSummary },
      ...(visit.passId ? { passId: visit.passId } : {}),
    };
  }

  return {
    kind: 'success',
    i18nKey: 'passLoyalty.visitRecorded',
    params: {
      stamps: visit.stampCount ?? 0,
      required: visit.visitsRequired ?? 0,
    },
    ...(visit.passId ? { passId: visit.passId } : {}),
  };
}
